import { cn } from './utils';
import { formatDayMonthYear } from './format';

/**
 * Color-coded badge for the listing.status enum (exchange listings — draft →
 * active → under_offer/pending_settlement → sold/expired/withdrawn).
 *
 * SIL-322: consolidated from 3 near-identical StatusBadge copies (customer
 * listings.tsx, customer listings_.$listingId.tsx, ops listings.tsx). Labels
 * follow ops's Title Case set (the pre-existing canonical choice); the
 * customer portal's lowercase copy ("active", "under offer", ...) changes
 * visibly as a deliberate consequence.
 */

const LABELS: Record<string, string> = {
  draft: 'Draft',
  active: 'Active',
  under_offer: 'Offer pending',
  pending_settlement: 'Pending settlement',
  sold: 'Sold',
  expired: 'Expired',
  withdrawn: 'Withdrawn',
};

// 669: pending_settlement is a committed forward sale — indigo, deliberately
// distinct from amber under_offer; expired is a neutral outcome (semantic
// muted tone), not an error (red).
const STYLES: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  active: 'bg-green-100 text-green-700',
  under_offer: 'bg-amber-100 text-amber-700',
  pending_settlement: 'bg-indigo-100 text-indigo-700',
  sold: 'bg-blue-100 text-blue-700',
  expired: 'bg-muted text-muted-foreground',
  withdrawn: 'bg-red-100 text-red-600',
};

/**
 * Pure label function — used directly by the badge and unit-tested. When
 * `availableOn` is a settlement date on a `pending_settlement` listing, the
 * label becomes "Settles {date}" (669's forward-sale rule) instead of the
 * static status label.
 */
export function listingStatusLabel(
  status: string,
  availableOn?: string | null,
): string {
  if (status === 'pending_settlement' && availableOn) {
    return `Settles ${formatDayMonthYear(availableOn)}`;
  }
  return LABELS[status] ?? status;
}

export function ListingStatusBadge({
  value,
  availableOn,
}: {
  value: string;
  availableOn?: string | null;
}) {
  const label = listingStatusLabel(value, availableOn);
  const style = STYLES[value] ?? 'bg-muted text-muted-foreground';
  return (
    <span
      className={cn(
        'inline-flex w-fit items-center rounded-full px-2 py-0.5 text-xs font-medium',
        style,
      )}
    >
      {label}
    </span>
  );
}
